/**
 * Fill resolved entity data into the reference pills `renderChatMarkdown`
 * produced, in place, inside a rendered message bubble.
 *
 * Everything written here goes in as `textContent` or an attribute — never as
 * markup — because the values come from entity titles a user typed.
 *
 * The function is idempotent: `data-ref-label` holds the pill's original text,
 * so re-running it (or running it after a reference stops resolving) restores
 * exactly what the markdown renderer produced.
 */

import type { ResolvedReference } from "../hooks/useEntityReferences";
import { humanise } from "./status";

const MAX_TITLE_LENGTH = 60;

function truncate(title: string): string {
  if (title.length <= MAX_TITLE_LENGTH) return title;
  return `${title.slice(0, MAX_TITLE_LENGTH - 1)}…`;
}

function setText(pill: HTMLAnchorElement, text: string): void {
  if (pill.textContent !== text) pill.textContent = text;
}

function setAttr(pill: HTMLAnchorElement, name: string, value: string): void {
  if (pill.getAttribute(name) !== value) pill.setAttribute(name, value);
}

function clear(pill: HTMLAnchorElement): void {
  setText(pill, pill.getAttribute("data-ref-label") ?? pill.textContent ?? "");
  pill.removeAttribute("data-ref-resolved");
  pill.removeAttribute("data-ref-status-category");
  pill.removeAttribute("data-ref-doc-status");
  // An unresolved pill previews nothing, so it must not be a navigable link
  // either: with no href there is nothing for middle-click, "open in new tab"
  // or a link drag to follow. `decorate` puts the href back on resolution.
  pill.removeAttribute("href");
  pill.removeAttribute("title");
}

function decorate(pill: HTMLAnchorElement, reference: ResolvedReference): void {
  if (reference.kind === "work_item") {
    const { item } = reference;
    setText(pill, `${item.display_id} · ${truncate(item.title)}`);
    setAttr(pill, "title", `${item.item_type} · ${item.status_name}`);
    setAttr(pill, "data-ref-status-category", item.status_category);
    pill.removeAttribute("data-ref-doc-status");
    setAttr(pill, "href", `/items/${item.id}`);
  } else {
    const { doc } = reference;
    setText(pill, truncate(doc.title));
    setAttr(
      pill,
      "title",
      `${humanise(doc.doc_type)} · ${humanise(doc.status)}`,
    );
    setAttr(pill, "data-ref-doc-status", doc.status);
    pill.removeAttribute("data-ref-status-category");
    setAttr(pill, "href", `/documents/${doc.id}`);
  }
  setAttr(pill, "data-ref-resolved", "true");
}

export function decorateReferences(
  container: HTMLElement,
  lookup: (key: string) => ResolvedReference | undefined,
): void {
  const pills = container.querySelectorAll<HTMLAnchorElement>(
    "a.entity-ref[data-ref-key]",
  );
  pills.forEach((pill) => {
    const reference = lookup(pill.getAttribute("data-ref-key") ?? "");
    if (reference) decorate(pill, reference);
    else clear(pill);
  });
}
